import { logger } from '@/utils/logger';
import { importWithRetry } from '@/utils/dynamicImport';
import { AGENT_CONFIGS } from './agentConfig';
import { SpecializedAgent, AgentRegistryProvider } from './types';
import { DEPARTMENTS } from './departments';

export class AgentRegistry implements AgentRegistryProvider {
    private agents: Map<string, SpecializedAgent> = new Map();
    private loaders: Map<string, () => Promise<SpecializedAgent>> = new Map();
    private metadata: Map<string, SpecializedAgent> = new Map();
    private loadErrors: Map<string, { error: Error; timestamp: number; attempts: number }> = new Map();
    private loadingPromises: Map<string, Promise<SpecializedAgent | undefined>> = new Map();
    private isInitialized = false;

    constructor() {
        this.initializeAgents();
    }

    /**
     * Pre-warm critical agents (call on app startup)
     */
    async warmup(): Promise<void> {
        if (this.isInitialized) return;

        try {
            logger.debug('[AgentRegistry] Pre-warming Generalist agent...');
            const generalist = await this.getAsync('generalist');
            if (generalist) {
                logger.debug('[AgentRegistry] Generalist agent pre-warmed successfully');
                this.isInitialized = true;
            } else {
                logger.error('[AgentRegistry] Failed to pre-warm Generalist agent');
            }
        } catch (e: unknown) {
            logger.error('[AgentRegistry] Warmup error:', e);
        }
    }

    private initializeAgents() {
        // Register Complex Agents (indii Conductor — Hub)
        // CRITICAL: Generalist MUST be registered first to ensure fallback availability
        try {
            const generalistKey = 'generalist';
            // Generalist metadata
            const meta = {
                id: generalistKey,
                name: 'indii Conductor',
                description: 'General assistance, complex reasoning, fallback.',
                color: '#fff',
                category: 'specialist',
                execute: async () => { throw new Error('Cannot execute metadata-only agent'); }
            } as SpecializedAgent;

            this.registerLazy(meta, async () => {
                const module = await importWithRetry(() => import('./specialists/GeneralistAgent'));
                if (!module.GeneralistAgent) {
                    throw new Error("Module imported but GeneralistAgent export is missing!");
                }
                const { SpecialistAgentFactory } = await importWithRetry(() => import('./builders/SpecialistAgentFactory'));
                return SpecialistAgentFactory.createSpecialistAgent(
                    generalistKey,
                    module.GeneralistAgent
                );
            });
        } catch (e: unknown) {
            logger.error("[AgentRegistry] CRITICAL: Failed to register GeneralistAgent:", e);
        }

        // Register Merchandise Agent (Class-based)
        try {
            const merchMeta = {
                id: 'merchandise',
                name: 'Merchandise Specialist',
                description: 'Intelligence-powered merchandise creation expert. Handles product design, mockup generation, video production, and manufacturing coordination.',
                color: '#FFE135',
                category: 'specialist',
                execute: async () => { throw new Error('Cannot execute metadata-only agent'); }
            } as SpecializedAgent;

            this.registerLazy(merchMeta, async () => {
                const { MerchandiseAgent } = await importWithRetry(() => import('./MerchandiseAgent'));
                const { SpecialistAgentFactory } = await importWithRetry(() => import('./builders/SpecialistAgentFactory'));
                return SpecialistAgentFactory.createSpecialistAgent('merchandise', MerchandiseAgent);
            });
        } catch (e: unknown) {
            logger.warn("[AgentRegistry] Failed to register MerchandiseAgent:", e);
        }

        // Register config-based agents
        if (AGENT_CONFIGS && Array.isArray(AGENT_CONFIGS)) {
            AGENT_CONFIGS.forEach(config => {
                try {
                    const meta = {
                        id: config.id,
                        name: config.name,
                        description: config.description,
                        color: config.color,
                        category: config.category,
                        execute: async () => { throw new Error('Cannot execute metadata-only agent'); }
                    } as SpecializedAgent;

                    this.registerLazy(meta, async () => {
                        const { SpecialistAgentFactory } = await importWithRetry(() => import('./builders/SpecialistAgentFactory'));
                        return SpecialistAgentFactory.createConfigAgent(config);
                    });
                } catch (e: unknown) {
                    logger.warn(`[AgentRegistry] Failed to register agent '${config?.id || 'unknown'}':`, e);
                }
            });
        }

        // Register Keeper (Context Integrity Guardian)
        try {
            const keeperMeta = {
                id: 'keeper',
                name: 'Keeper',
                description: 'Context Integrity Guardian. Maintains coherence and recalls critical context/rules.',
                color: '#4B0082', // Indigo
                category: 'specialist',
                execute: async () => { throw new Error('Cannot execute metadata-only agent'); }
            } as SpecializedAgent;

            this.registerLazy(keeperMeta, async () => {
                const { SpecialistAgentFactory } = await importWithRetry(() => import('./builders/SpecialistAgentFactory'));
                return SpecialistAgentFactory.createBaseAgent({
                    id: 'keeper',
                    name: 'Keeper',
                    description: 'Context Integrity Guardian',
                    color: '#4B0082',
                    category: 'specialist',
                    systemPrompt: 'You are Keeper, the Context Integrity Guardian for indii. Your goal is to ensure all agent interactions are coherent, adhere to brand guidelines, and recall necessary memories or rules.',
                    tools: []
                });
            });
        } catch (e: unknown) {
            logger.warn("[AgentRegistry] Failed to register Keeper agent:", e);
        }

        // Register Curriculum Agent (Music Business Education)
        try {
            const curriculumMeta = {
                id: 'curriculum',
                name: 'Music Education Specialist',
                description: 'Teaches independent artists the music business — copyright, royalties, contracts, distribution, and building a sustainable career.',
                color: '#FF4081',
                category: 'specialist',
                execute: async () => { throw new Error('Cannot execute metadata-only agent'); }
            } as SpecializedAgent;

            this.registerLazy(curriculumMeta, async () => {
                const { CurriculumAgent } = await importWithRetry(() => import('./specialists/CurriculumAgent'));
                const { SpecialistAgentFactory } = await importWithRetry(() => import('./builders/SpecialistAgentFactory'));
                return SpecialistAgentFactory.createSpecialistAgent('curriculum', CurriculumAgent);
            });
        } catch (e: unknown) {
            logger.warn("[AgentRegistry] Failed to register CurriculumAgent:", e);
        }



        // Register metadata-only worker entries from DEPARTMENTS
        try {
            for (const dept of Object.values(DEPARTMENTS)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const typedDept = dept as any;
                for (const workerId of typedDept.workerIds) {
                    // Skip if the worker was already registered explicitly via AGENT_CONFIGS
                    if (this.metadata.has(workerId)) continue;
                    try {
                        const workerMeta = {
                            id: workerId,
                            name: `${typedDept.displayName} Worker: ${workerId.split('.')[1]}`,
                            description: `Worker agent for the ${typedDept.displayName} department.`,
                            color: '#666',
                            category: 'specialist',
                            execute: async () => { throw new Error('Cannot execute metadata-only worker agent'); }
                        } as SpecializedAgent;

                        this.registerLazy(workerMeta, async () => {
                            const { SpecialistAgentFactory } = await importWithRetry(() => import('./builders/SpecialistAgentFactory'));
                            return SpecialistAgentFactory.createBaseAgent({
                                id: workerId,
                                name: workerMeta.name,
                                description: workerMeta.description,
                                color: workerMeta.color,
                                category: 'specialist',
                                systemPrompt: `You are a worker in the ${typedDept.displayName} department.`,
                                tools: []
                            });
                        });
                    } catch (e: unknown) {
                        logger.warn(`[AgentRegistry] Failed to register worker agent '${workerId}':`, e);
                    }
                }
            }
        } catch (e: unknown) {
            logger.warn("[AgentRegistry] Failed to register worker agents:", e);
        }
    }

    register(agent: SpecializedAgent) {
        this.agents.set(agent.id, agent);
        this.metadata.set(agent.id, agent);
    }

    registerLazy(meta: SpecializedAgent, loader: () => Promise<SpecializedAgent>) {
        this.metadata.set(meta.id, meta);
        this.loaders.set(meta.id, loader);
    }

    get(id: string): SpecializedAgent | undefined {
        // Return loaded agent or metadata-only entry
        return this.agents.get(id) || this.metadata.get(id);
    }

    async getAsync(id: string): Promise<SpecializedAgent | undefined> {
        // Return cached agent if already loaded
        if (this.agents.has(id)) {
            return this.agents.get(id);
        }

        // Deduplicate concurrent loads
        if (this.loadingPromises.has(id)) {
            return this.loadingPromises.get(id);
        }

        const loader = this.loaders.get(id);
        if (!loader) {
            logger.warn(`[AgentRegistry] No loader registered for agent '${id}'`);
            return undefined;
        }

        const loadPromise = (async (): Promise<SpecializedAgent | undefined> => {
            try {
                logger.debug(`[AgentRegistry] Loading agent '${id}'...`);
                // Use the unified dynamic import retry helper for lazy-loaded agents
                const agent = await importWithRetry(loader);
                this.agents.set(id, agent);
                this.loadErrors.delete(id);
                logger.debug(`[AgentRegistry] Agent '${id}' loaded successfully`);
                return agent;
            } catch (e: unknown) {
                const error = e instanceof Error ? e : new Error(String(e));
                logger.error(`[AgentRegistry] Agent '${id}' PERMANENTLY failed after retries. Error: ${error.message}`);
                
                // Track error state - note: importWithRetry already attempted 3-4 times internally
                this.loadErrors.set(id, { error, timestamp: Date.now(), attempts: 4 });
                return undefined;
            }
        })();

        this.loadingPromises.set(id, loadPromise);
        
        // Cleanup promise from map once settled to allow future re-loads if it failed
        loadPromise.finally(() => {
            if (!this.agents.has(id)) {
                this.loadingPromises.delete(id);
            }
        });

        return loadPromise;
    }

    /**
     * Get the last error for an agent (useful for debugging)
     */
    getLoadError(id: string): { error: Error; timestamp: number; attempts: number } | undefined {
        return this.loadErrors.get(id);
    }

    getAll(): SpecializedAgent[] {
        // Returns mixed active agents and metadata-only entries
        // Use with caution for execution. Use for listing capabilities.
        return Array.from(this.metadata.values());
    }

    listCapabilities(): string {
        return Array.from(this.metadata.values())
            .map(a => `- ${a.name} (${a.id}): ${a.description}`)
            .join('\n');
    }
}

export const agentRegistry = new AgentRegistry();
if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).agentRegistry = agentRegistry;
}
